import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiArrowLeft,
    FiZap,
    FiHeart,
    FiBriefcase,
    FiDollarSign,
    FiActivity,
    FiHome,
    FiXCircle,
    FiCheckCircle,
    FiAlertTriangle,
    FiLock,
    FiStar
} from "react-icons/fi";
import ModuleHeader from "../../moduleHeader";
import { getCurrentPackageUserModule, getTransitAnalysis, postTransitAnalysis } from "../../../services/api";

const PERIODS = [
    { id: "gunluk", label: "Günlük", tokens: 25 },
    { id: "haftalik", label: "Haftalık", tokens: 50 },
    { id: "aylik", label: "Aylık", tokens: 100 },
    { id: "yillik", label: "Yıllık", tokens: 250 }
];

const TYPE_MAP = {
    gunluk: "daily",
    haftalik: "weekly",
    aylik: "monthly",
    yillik: "yearly"
};

const CATEGORIES = [
    {
        id: "love",
        title: "İlişkiler & Aşk",
        icon: <FiHeart />,
        color: "from-pink-400 to-rose-500",
        bgColor: "bg-rose-50",
        textColor: "text-rose-600",
        planets: "Venüs, Mars, 7. Ev",
        content: "Ayrılık / yakınlaşma süreçleri, eski ilişkilerin gündeme gelmesi ve duygusal dengelenme dönemindesiniz."
    },
    {
        id: "career",
        title: "Kariyer & İş",
        icon: <FiBriefcase />,
        color: "from-blue-400 to-indigo-500",
        bgColor: "bg-blue-50",
        textColor: "text-blue-600",
        planets: "Satürn, Mars, 10. Ev",
        content: "Patronlarla ilişkiler, yeni sorumluluklar ve iş değişimi temaları bu dönemde ön planda."
    },
    {
        id: "finance",
        title: "Para & Maddi Konular",
        icon: <FiDollarSign />,
        color: "from-emerald-400 to-teal-500",
        bgColor: "bg-emerald-50",
        textColor: "text-emerald-600",
        planets: "Jüpiter, Venüs, 2-8. Ev",
        content: "Harcama uyarıları ve yatırım kararları için kritik bir eşik. Maddi kaynaklarınızı koruyun."
    },
    {
        id: "psychology",
        title: "Psikoloji & Ruh Hali",
        icon: <FiActivity />,
        color: "from-purple-400 to-fuchsia-500",
        bgColor: "bg-purple-50",
        textColor: "text-purple-600",
        planets: "Ay, Neptün, 12. Ev",
        content: "Kaygı ve dalgınlık hissedilebilir. İçe dönüş ihtiyacı ve meditasyon için uygun bir zaman."
    },
    {
        id: "family",
        title: "Aile & Özel Hayat",
        icon: <FiHome />,
        color: "from-orange-400 to-amber-500",
        bgColor: "bg-orange-50",
        textColor: "text-orange-600",
        planets: "4. Ev, Ay",
        content: "Ev değişikliği, ebeveynlerle ilgili gündemler ve taşınma teması hayatınızda yer kaplayabilir."
    }
];

const CHECKLIST = [
    {
        type: "uzak",
        label: "Uzak Dur",
        icon: <FiXCircle />,
        color: "text-red-500",
        items: ["Tartışmalar", "Fevri kararlar", "Büyük harcamalar"]
    },
    {
        type: "uygun",
        label: "Uygun Zaman",
        icon: <FiCheckCircle />,
        color: "text-emerald-500",
        items: ["Görüşme yapmak", "Başvuru göndermek", "Konuşmak / barışmak"]
    },
    {
        type: "dikkat",
        label: "Dikkat",
        icon: <FiAlertTriangle />,
        color: "text-amber-500",
        items: ["İletişim kazaları", "Sağlık", "Yorgunluk"]
    }
];

const TransitAnalysis = ({ id: propId, moduleId }) => {
    const { id: routeId } = useParams();
    const id = routeId || propId;
    const navigate = useNavigate();

    const [activePeriod, setActivePeriod] = useState("gunluk");
    const [hasAnalysis, setHasAnalysis] = useState(false);
    const [analysisData, setAnalysisData] = useState(null);
    const [analysisTargetDate, setAnalysisTargetDate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [packageData, setPackageData] = useState(null);
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            try {
                const data = await getCurrentPackageUserModule(id, 27);
                setPackageData(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    useEffect(() => {
        loadAnalysis(activePeriod);
    }, [activePeriod]);

    const loadAnalysis = async (period) => {
        setLoadingAnalysis(true);
        setHasAnalysis(false);
        setAnalysisData(null);
        try {
            const type = TYPE_MAP[period];
            const res = await getTransitAnalysis(type);
            if (res.success && res.data && res.data.data.length > 0) {
                setAnalysisData(res.data.data[0].data);
                setHasAnalysis(true);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingAnalysis(false);
        }
    };

    const handleRequestAnalysis = async () => {
        setIsAnalyzing(true);
        setAnalysisProgress(0);

        const type = TYPE_MAP[activePeriod];

        let progress = 0;
        const timer = setInterval(() => {
            progress += 3;
            if (progress <= 95) setAnalysisProgress(progress);
        }, 300);

        try {
            const res = await postTransitAnalysis(type);
            clearInterval(timer);
            setAnalysisProgress(100);

            setTimeout(() => {
                if (res.success && res.data && res.data.data) {
                    setAnalysisData(res.data.data);
                    setAnalysisTargetDate(res.data.target_date);
                    setHasAnalysis(true);
                } else {
                    alert(res.message || "Analiz alınamadı.");
                }
                setIsAnalyzing(false);
            }, 600);
        } catch (err) {
            clearInterval(timer);
            setIsAnalyzing(false);
            alert("Analiz işlemi sırasında bir hata oluştu.");
        }
    };

    const currentPeriodData = PERIODS.find(p => p.id === activePeriod);

    if (loading) return null;

    return (
        <div className="w-full h-screen min-h-screen flex flex-col items-center bg-[#F8F9FB] overflow-hidden">
            <div className="flex flex-col items-center w-full h-full sm:max-w-[390px] bg-white sm:rounded-[40px] shadow-2xl relative overflow-y-auto no-scrollbar">

                {/* Module Header - Following Numerology Pattern */}
                {packageData && (
                    <div className="relative z-30 w-full mb-2">
                        <ModuleHeader
                            userName={
                                packageData.user?.first_name + " " + (packageData.user?.last_name || "")
                            }
                            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
                            title={packageData.package?.name}
                            description={packageData.package?.description}
                            backgroundImage={packageData.package?.image_url}
                            onExpandedChange={setIsHeaderExpanded}
                            showToggle={true}
                            autoHeight={true}
                        />
                    </div>
                )}

                <div className={`w-full px-6 py-4 pb-32 flex-1 bg-white relative z-20 transition-all duration-300 ${isHeaderExpanded ? "mt-0" : "-mt-6 rounded-t-[40px] shadow-[0_-20px_40px_rgba(0,0,0,0.05)]"}`}>
                    {/* Period Selector */}
                    <div className="bg-gray-100/80 p-1.5 rounded-3xl flex items-center relative gap-1 mb-8">
                        {PERIODS.map((p) => {
                            const isActive = activePeriod === p.id;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => setActivePeriod(p.id)}
                                    className={`relative flex-1 py-2 rounded-2xl text-[10px] font-black transition-all duration-500 ${isActive ? "text-white" : "text-gray-500"
                                        }`}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="transitTab"
                                            className="absolute inset-0 bg-[#3DA7B5] rounded-2xl shadow-lg shadow-[#3DA7B5]/20"
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}
                                    <span className="relative z-10">{p.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    <AnimatePresence mode="wait">
                        {isAnalyzing ? (
                            <motion.div
                                key="analyzing"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex flex-col items-center justify-center py-20 space-y-8"
                            >
                                <div className="relative w-44 h-44">
                                    <svg className="w-full h-full rotate-[-90deg]">
                                        <circle
                                            cx="88"
                                            cy="88"
                                            r="80"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            fill="transparent"
                                            className="text-gray-100"
                                        />
                                        <motion.circle
                                            cx="88"
                                            cy="88"
                                            r="80"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            fill="transparent"
                                            strokeDasharray="502.4"
                                            animate={{ strokeDashoffset: 502.4 - (502.4 * analysisProgress) / 100 }}
                                            className="text-[#3DA7B5]"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <motion.div
                                            animate={{ scale: [1, 1.2, 1], rotate: 360 }}
                                            transition={{
                                                scale: { repeat: Infinity, duration: 2 },
                                                rotate: { repeat: Infinity, duration: 4, ease: "linear" }
                                            }}
                                            className="text-[#3DA7B5] text-4xl mb-1"
                                        >
                                            <FiZap className="fill-[#3DA7B5]" />
                                        </motion.div>
                                        <span className="text-2xl font-black text-gray-800">%{Math.round(analysisProgress)}</span>
                                    </div>
                                </div>

                                <div className="text-center space-y-2">
                                    <h3 className="text-gray-900 font-black text-lg">Yıldızlar Konuşuyor...</h3>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-gray-500 text-xs font-medium italic min-h-[1.5rem]">
                                            {analysisProgress < 30 && "Doğum haritanız taranıyor..."}
                                            {analysisProgress >= 30 && analysisProgress < 60 && "Gezegen transitleri hesaplanıyor..."}
                                            {analysisProgress >= 60 && analysisProgress < 90 && "Ev konumları analiz ediliyor..."}
                                            {analysisProgress >= 90 && "Yorumlar hazır hale getiriliyor..."}
                                        </p>
                                        <div className="flex justify-center gap-1 mt-2">
                                            {[0, 1, 2].map((i) => (
                                                <motion.div
                                                    key={i}
                                                    animate={{ opacity: [0.3, 1, 0.3] }}
                                                    transition={{ repeat: Infinity, duration: 1, delay: i * 0.2 }}
                                                    className="w-1.5 h-1.5 rounded-full bg-[#3DA7B5]"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ) : loadingAnalysis ? (
                            <motion.div
                                key="loading"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex justify-center items-center py-20"
                            >
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3DA7B5]"></div>
                            </motion.div>
                        ) : !hasAnalysis || !analysisData ? (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="flex flex-col items-center text-center space-y-6 py-12"
                            >
                                <div className="w-24 h-24 bg-[#3DA7B5]/10 rounded-[30%] flex items-center justify-center relative">
                                    <FiLock className="text-[#3DA7B5] text-4xl" />
                                    <motion.div
                                        animate={{ scale: [1, 1.2, 1] }}
                                        transition={{ repeat: Infinity, duration: 2 }}
                                        className="absolute -top-1 -right-1"
                                    >
                                        <FiZap className="text-amber-400 fill-amber-400 text-xl" />
                                    </motion.div>
                                </div>

                                <div className="space-y-2">
                                    <h3 className="text-gray-900 font-black text-lg">Analiz Bekleniyor</h3>
                                    <p className="text-gray-500 text-xs leading-relaxed max-w-[240px] mx-auto">
                                        Henüz {currentPeriodData.label.toLowerCase()} transit analizi talebiniz bulunmuyor. Gökyüzü etkilerini öğrenmek için talep oluşturun.
                                    </p>
                                </div>

                                <div className="w-full grid gap-3">
                                    <button
                                        onClick={handleRequestAnalysis}
                                        className="w-full py-4 bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] rounded-2xl text-white font-black text-sm shadow-xl shadow-[#3DA7B5]/30 flex items-center justify-center gap-3 active:scale-95 transition-transform"
                                    >
                                        <FiStar className="fill-white" />
                                        {currentPeriodData.label} Analiz Talep Et ({currentPeriodData.tokens} Token)
                                    </button>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">veya Abonesen Ücretsiz</p>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="content"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="space-y-8"
                            >
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">{currentPeriodData.label} Transit Analizi</h4>
                                    <div className="space-y-4">
                                        {Object.entries(analysisData).map(([key, content], idx) => {
                                            if (!content || key === 'daily') return null;

                                            // Determine title logic
                                            let title = "Önemli Tavsiye";
                                            let icon = <FiStar />;
                                            let iconBg = "from-indigo-400 to-purple-500";
                                            let iconShadow = "bg-indigo-50";

                                            // Map the daily categories
                                            if (key === "ask") {
                                                title = "Aşk ve İlişkiler";
                                                icon = <FiHeart />;
                                                iconBg = "from-rose-400 to-pink-500";
                                                iconShadow = "bg-rose-50";
                                            } else if (key === "kariyer") {
                                                title = "Kariyer ve İş";
                                                icon = <FiBriefcase />;
                                                iconBg = "from-blue-400 to-cyan-500";
                                                iconShadow = "bg-blue-50";
                                            } else if (key === "finans") {
                                                title = "Finans ve Para";
                                                icon = <FiDollarSign />;
                                                iconBg = "from-emerald-400 to-teal-500";
                                                iconShadow = "bg-emerald-50";
                                            } else if (key === "saglik") {
                                                title = "Sağlık ve Kişisel Bakım";
                                                icon = <FiActivity />;
                                                iconBg = "from-orange-400 to-amber-500";
                                                iconShadow = "bg-orange-50";
                                            } else if (key === "kararlar") {
                                                title = "Günlük Kararlar";
                                                icon = <FiZap />;
                                                iconBg = "from-violet-400 to-fuchsia-500";
                                                iconShadow = "bg-violet-50";
                                            }

                                            // Map periods
                                            if (key.includes("day_")) {
                                                const dayIdx = parseInt(key.replace('day_', '')) - 1;
                                                const dayDate = analysisTargetDate ? new Date(analysisTargetDate) : new Date();
                                                dayDate.setDate(dayDate.getDate() + dayIdx);
                                                title = dayDate.toLocaleDateString("tr-TR", { weekday: 'long', day: 'numeric', month: 'long' });
                                                icon = <FiZap />;
                                            } else if (key.includes("week_")) {
                                                title = `${key.replace('week_', '')}. Hafta`;
                                                icon = <FiStar />;
                                            } else if (key.includes("month_")) {
                                                title = `${key.replace('month_', '')}. Ay`;
                                                icon = <FiStar />;
                                            }

                                            return (
                                                <motion.div
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: idx * 0.1 }}
                                                    key={key}
                                                    className="bg-white border border-gray-100 rounded-[2rem] p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group text-left"
                                                >
                                                    <div className={`absolute -top-1 -right-1 p-6 rounded-bl-[3rem] ${iconShadow} opacity-20 group-hover:opacity-30 transition-opacity`}></div>
                                                    <div className="flex items-start gap-4 transition-transform group-hover:translate-x-1 duration-300">
                                                        <div className={`p-3 rounded-2xl bg-gradient-to-br ${iconBg} text-white shadow-lg`}>
                                                            {icon}
                                                        </div>
                                                        <div className="flex-1 space-y-1">
                                                            <h5 className="text-gray-900 font-black text-sm">{title}</h5>
                                                            <p className="text-gray-600 text-[13px] leading-relaxed font-light mt-2">{content}</p>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default TransitAnalysis;
